import { useEffect, useEffectEvent, useRef, type DependencyList } from "react";
import { AppState, type AppStateStatus } from "react-native";

type AppStateChangeHandler = (nextState: AppStateStatus) => void | (() => void);

/**
 * Like `useEffect`, but runs on mount, when `deps` change, and on app state
 * transitions. The handler always sees the latest closure via `useEffectEvent`;
 * list values it reads in `deps`, same as with `useEffect`.
 *
 * Linted at call sites via `react-hooks/exhaustive-deps` `additionalHooks`.
 */
const useAppStateEffect = (
  onAppStateChange: AppStateChangeHandler,
  deps?: DependencyList,
) => {
  const onAppStateChangeRef = useEffectEvent(onAppStateChange);

  const cleanupRef = useRef<void | (() => void) | null>(null);

  useEffect(() => {
    const run = (nextState: AppStateStatus) => {
      cleanupRef.current?.();
      cleanupRef.current = onAppStateChangeRef(nextState);
    };

    run(AppState.currentState);

    const subscription = AppState.addEventListener("change", run);

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      subscription.remove();
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useAppStateEffect;
